import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useI18n } from '@/i18n/i18n-provider';
import { authApi } from '@/services/auth.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button } from '@/components/ui';
import { LocalePicker } from '@/components/locale-picker';
import { LogoMark } from '@/components/logo-mark';

type Status = 'verifying' | 'done' | 'error';

/**
 * Redeems the link `sendEmailVerification` mailed out. Nothing here gates
 * access — a signed-in user can keep using every module whether or not this
 * page is ever visited; it only confirms the address is real.
 */
export function VerifyEmailPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [status, setStatus] = useState<Status>(token ? 'verifying' : 'error');
  const [error, setError] = useState<string | null>(null);
  const attempted = useRef(false);

  useEffect(() => {
    if (!token || attempted.current) return;
    attempted.current = true;
    void verify();

    async function verify(): Promise<void> {
      try {
        await authApi.verifyEmail(token);
        setStatus('done');
      } catch (caught) {
        const code = caught instanceof ApiError ? caught.code : 'INTERNAL_ERROR';
        setError(
          code === 'AUTH_TOKEN_EXPIRED'
            ? t('auth.verificationLinkExpired')
            : t('auth.verificationLinkInvalid'),
        );
        setStatus('error');
      }
    }
  }, [token, t]);

  return (
    <main className="auth-page">
      <div className="auth-card">
        <header className="auth-header">
          <LogoMark size={40} />
          <h1 className="auth-title">TABITA</h1>
        </header>

        {status === 'verifying' && <p>{t('auth.verifyingEmail')}</p>}

        {status === 'done' && (
          <>
            <Alert tone="success">{t('auth.emailVerified')}</Alert>
            <Button
              type="button"
              variant="primary"
              onClick={() => void navigate('/', { replace: true })}
            >
              {t('auth.continueToApp')}
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <Alert tone="danger">{error ?? t('auth.verificationLinkInvalid')}</Alert>
            <Button
              type="button"
              variant="primary"
              onClick={() => void navigate('/', { replace: true })}
            >
              {t('auth.continueToApp')}
            </Button>
          </>
        )}

        <footer className="auth-footer">
          <LocalePicker />
        </footer>
      </div>
    </main>
  );
}
